import {
  Button,
  Col,
  DatePicker,
  Form,
  FormInstance,
  Input,
  message,
  Row,
  Select,
  Space,
  Steps,
  TimePicker,
  Typography,
  Upload,
  UploadProps,
} from 'antd';
import { useCallback, useEffect, useRef, useState, useContext, ChangeEvent } from 'react';
import { Field, Grid, GridRow } from '../../models';
import { apiClient, apiErrorMessage, getApiBaseUrl, parseHyperlink } from '../../utils';
import { DebounceSelect, LocalImage, TextEditor } from '../../elements';
import { hasSetWorkflowStatusPermission } from '../../utils';
import { UserContext } from '../contexts';
import { InboxOutlined } from '@ant-design/icons';

export function FieldFormItem(props: {
  form: FormInstance<any>;
  gridId?: string;
  field?: Field;
  recordId?: string;
  name?: string;
  label?: string;
  hideLabel?: boolean;
  hideTooltip?: boolean;
  allowRecordWithErrors?: boolean;
  notRequired?: boolean;
  value?: any;
  layout?: any;
  predictions?: { value: string; confidence: string }[];
  showPredictionError?: boolean;
  record?: GridRow;
}) {
  const [fkOptions, setFkOptions] = useState<{ value: string; label: string }[]>();
  const [fkOptionsLoaded, setFkOptionsLoaded] = useState(false);
  const [fkOptionsLoading, setFkOptionsLoading] = useState(false);
  const [searchable, setSearchable] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>();
  const [predictions, setPredictions] = useState<{ value: string; confidence: string }[]>();
  const [showPredictionError, setShowPredictionError] = useState<boolean>(false);
  const [form, setForm] = useState<FormInstance<any>>();
  const stringInputRef = useRef(null);
  const [templateSelectedValues, setTemplateSelectedValues] = useState<string[]>([]);
  const [grid, setGrid] = useState<Grid>();
  const [record, setRecord] = useState<GridRow>();

  const { TextArea } = Input;
  const { Text } = Typography;

  const { user } = useContext(UserContext);
  const workflowPermission = hasSetWorkflowStatusPermission(user, props.field);

  const baseApiUrl = getApiBaseUrl() + '/api/';

  const itemName = props.name || props.field?.id;
  const itemTooltip = props.hideTooltip ? undefined : parseHyperlink(props.field?.description);

  useEffect(() => {
    setForm(props.form);
  }, [props.form]);

  const loadForeignKeyOptions = useCallback(
    async (search: string) => {
      if (fkOptionsLoaded && search?.trim().length < 2) {
        return [];
      }

      setFkOptionsLoading(true);
      let gridId: string | undefined = '';

      if (props.field?.dataType === 'FOREIGNKEY' || props.field?.dataType === 'MULTIFOREIGNKEY') {
        gridId = props.field?.foreignKeyGridId;
      }

      const queryParams = [
        `sourceGridId=${props.gridId}`,
        `sourceFieldId=${props.field?.id}`,
        `gridId=${gridId}`,
        `summaryFieldId=${props.field?.foreignKeyFieldId}`,
      ];
      if (search?.trim().length > 1) {
        queryParams.push(`search=${search}`);
      }

      try {
        const response = await apiClient.get(`records/rows/foreign-keys?${queryParams.join('&')}`);
        const responseFkOptions = response.data.options as { value: string; label: string }[];
        const responseFkOptionsCount = response.data.totalCount as number;
        setFkOptions(responseFkOptions);

        let returnEmpty = false;
        if (!fkOptionsLoaded) {
          if (responseFkOptionsCount > 1000) {
            setSearchable(true);
            returnEmpty = true;
          }
        }
        setFkOptionsLoaded(true);
        setFkOptionsLoading(false);

        return returnEmpty ? [] : responseFkOptions;
      } catch (reason: any) {
        setFkOptionsLoading(false);
        apiErrorMessage(reason);
        return [];
      }
    },
    [
      fkOptionsLoaded,
      props.field?.dataType,
      props.field?.id,
      props.field?.foreignKeyFieldId,
      props.field?.foreignKeyGridId,
      props.gridId,
    ],
  );

  useEffect(() => {
    if (props.field?.code !== 'PO_CATEGORY_SEGMENT_COMBINATION') {
      return;
    }

    const gridId = props.field?.gridId;
    apiClient
      .get(`grids/${gridId}`)
      .then((response: any) => {
        const responseGrid = response.data as Grid;
        setGrid(responseGrid);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });

    apiClient
      .get(`records/record?gridId=${props.gridId}&rowId=${props.recordId}`)
      .then((response: any) => {
        const responseRecord = response.data as GridRow;
        setRecord(responseRecord);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  }, [props.field, props.gridId, props.recordId]);

  useEffect(() => {
    if (
      (props.field?.dataType === 'FOREIGNKEY' && !props.field?.mainForeignKeyFieldId) ||
      (props.field?.dataType === 'MULTIFOREIGNKEY' && !props.field?.mainForeignKeyFieldId)
    ) {
      loadForeignKeyOptions('');
    }
  }, [loadForeignKeyOptions, props.field?.dataType, props.field?.foreignKeyGridId, props.field?.mainForeignKeyFieldId, props.gridId]);

  const selectChange = (selectedIds: any) => {
    if (props.field?.dataType === 'MULTIPICKLIST' || props.field?.dataType === 'PICKLIST') {
      if (Array.isArray(selectedIds)) {
        const selectedValues = selectedIds.map((id: string) => {
          const selectedOption = props.field?.picklistValues?.find(pv => pv.id === id);
          return selectedOption?.val || '';
        });
        setTemplateSelectedValues(selectedValues);
      } else {
        const selectedOption = props.field?.picklistValues?.find(pv => pv.id === selectedIds);
        setTemplateSelectedValues([selectedOption?.val || '']);
      }
    }
    if (props.field?.dataType === 'MULTIFOREIGNKEY' || props.field?.dataType === 'FOREIGNKEY') {
      if (Array.isArray(selectedIds)) {
        const selectedValues = selectedIds.map((id: string) => {
          const selectedOption = fkOptions?.find(pv => pv.value === id);
          return selectedOption?.label || '';
        });
        setTemplateSelectedValues(selectedValues);
      } else {
        const selectedOption = fkOptions?.find(pv => pv.value === selectedIds);
        setTemplateSelectedValues([selectedOption?.label || '']);
      }
    }
  };

  const handleTemplateUpdate = (value: string[]) => {
    form?.setFieldsValue({ [props.field?.id + '_templateValues']: value });
  };

  useEffect(() => {
    setPredictions(props.predictions);
    setShowPredictionError(props.showPredictionError || false);
  }, [props.predictions, props.showPredictionError]);

  const changeTextAreaSize = (e: any) => {
    e.target.style.height = 'auto';
    if (e.target.scrollHeight > e.target.clientHeight) {
      e.target.style.height = e.target.scrollHeight + 'px';
    }
    if (e.target.scrollHeight < e.target.clientHeight) {
      e.target.style.height = e.target.scrollHeight + 'px';
    }
  };

  const onTextAreaChange = (e: any) => {
    changeTextAreaSize(e);
  };

  const onSearch = (e: any) => {
    changeTextAreaSize(e);

    const value = e.target?.value;
    if (
      value.length > 2 &&
      !(
        grid?.code === 'ZZ_PLM_KALEM_TABLOSU' &&
        props.field?.code === 'PO_CATEGORY_SEGMENT_COMBINATION' &&
        record?.cells?.[props.field?.id]?.value === value
      )
    ) {
      apiClient
        .get(`suggestions?gridId=${props.gridId}&fieldId=${props.field?.id}&hint=${value}`)
        .then((response: any) => {
          const suggestionsData: string[] = response.data.filter((d: any) => d.value !== null).map((d: any) => d.value as string);
          setSuggestions(suggestionsData);
        })
        .catch(() => {});
    } else {
      setSuggestions(undefined);
    }
  };

  const onSuggestionClick = (value: string) => {
    if (!props.field) {
      return;
    }
    const newValues: any = {};
    newValues[props.field.id] = value;
    form?.setFieldsValue(newValues);
    if (stringInputRef.current) {
      (stringInputRef.current as any).resizableTextArea.textArea.value = value;
    }
  };

  const onPredictionClick = (value: string, fullValue: string) => {
    if (!props.field) {
      return;
    }
    const newValues: any = {};
    if (props.field.dataType === 'MULTIPICKLIST') {
      const currentValues = form?.getFieldValue(props.field.id);
      const picklistId = props.field.picklistValues?.find(pv => pv.val === value)?.id;
      if (currentValues?.includes(picklistId)) {
        return;
      } else {
        newValues[props.field.id] = [picklistId, ...currentValues];
      }
    } else if (props.field.dataType === 'MULTIFOREIGNKEY') {
      const currentValues = form?.getFieldValue(props.field.id);
      const fkOption = fkOptions?.find(o => o.label === value);
      if (currentValues?.includes(fkOption?.value)) {
        return;
      } else {
        newValues[props.field.id] = [fkOption?.value, ...currentValues];
      }
    } else if (props.field.dataType === 'FOREIGNKEY') {
      const fkOption = fkOptions?.find(o => o.label === value);
      newValues[props.field.id] = [fkOption?.value];
    } else {
      newValues[props.field.id] = value;
    }
    form?.setFieldsValue(newValues);
    if (stringInputRef.current) {
      (stringInputRef.current as any).resizableTextArea.textArea.value = value;
    }
  };

  if (!props.field) {
    return null;
  }

  const rules = [] as any[];

  if (
    ((props.field.isRequired && !props.allowRecordWithErrors) || (props.field.isIdentifier && !props.field.defaultValue)) &&
    !props.notRequired
  ) {
    rules.push({ required: true, message: 'Zorunlu alan' });
  }

  const UnitOfMeasureSelect = () => {
    const uomName = props.field?.id + '_uom';
    return props.field?.unitsOfMeasure ? (
      <Form.Item name={uomName}>
        <Select>
          <Select.Option key="none" value="">
            {' '}
          </Select.Option>
          {props.field?.unitsOfMeasure.map(unit => (
            <Select.Option key={unit} value={unit}>
              {unit}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    ) : null;
  };

  const ItemLabel = () => {
    return props.hideLabel ? null : (
      <>
        {props.field?.isRequired && !props.field?.isIdentifier && props.allowRecordWithErrors && (
          <Text
            type="danger"
            style={{
              marginRight: '0.25rem',
            }}
          >
            *
          </Text>
        )}
        <span style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{props.label || props.field?.displayName || props.field?.name}</span>
      </>
    );
  };

  if (props.field.dataType === 'AUTOINC') {
    return null;
  }

  if (props.field.dataType === 'INTEGER') {
    return !props.field?.unitsOfMeasure ? (
      <>
        <Form.Item
          label={<ItemLabel />}
          name={itemName}
          tooltip={itemTooltip}
          rules={[...rules, { pattern: '^([+-]?[1-9][0-9]*|0)$', message: 'Geçersiz tam sayı' }]}
        >
          <Input style={{ width: '100%' }} />
        </Form.Item>
      </>
    ) : (
      <Row gutter={8} style={{ alignItems: 'flex-end' }}>
        <Col span={16}>
          <Form.Item
            label={<ItemLabel />}
            name={itemName}
            tooltip={itemTooltip}
            labelCol={props.layout !== 'vertical' ? { span: 6 } : undefined}
            rules={[...rules, { pattern: '^([+-]?[1-9][0-9]*|0)$', message: 'Geçersiz tam sayı' }]}
          >
            <Input style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <UnitOfMeasureSelect />
        </Col>
      </Row>
    );
  }

  if (props.field.dataType === 'DOUBLE') {
    return !props.field?.unitsOfMeasure ? (
      <>
        <Form.Item
          label={<ItemLabel />}
          name={itemName}
          tooltip={itemTooltip}
          rules={[...rules, { pattern: '^[+-]?[0-9]*[.]?[0-9]+$', message: 'Geçersiz ondalıklı sayı' }]}
        >
          <Input style={{ width: '100%' }} />
        </Form.Item>
      </>
    ) : (
      <Row gutter={8} style={{ alignItems: 'flex-end' }}>
        <Col span={16}>
          <Form.Item
            label={<ItemLabel />}
            labelCol={props.layout !== 'vertical' ? { span: 6 } : undefined}
            name={itemName}
            tooltip={itemTooltip}
            rules={[...rules, { pattern: '^[+-]?[0-9]*[.]?[0-9]+$', message: 'Geçersiz ondalıklı sayı' }]}
          >
            <Input style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <UnitOfMeasureSelect />
        </Col>
      </Row>
    );
  }

  if (props.field.dataType === 'DECIMAL') {
    return !props.field?.unitsOfMeasure ? (
      <>
        <Form.Item
          label={<ItemLabel />}
          name={itemName}
          tooltip={itemTooltip}
          rules={[...rules, { pattern: '^[+-]?[0-9]*[.]?[0-9]+$', message: 'Geçersiz ondalıklı sayı' }]}
        >
          <Input style={{ width: '100%' }} />
        </Form.Item>
      </>
    ) : (
      <Row gutter={8} style={{ alignItems: 'flex-end' }}>
        <Col span={18}>
          <Form.Item
            label={<ItemLabel />}
            labelCol={props.layout !== 'vertical' ? { span: 6 } : undefined}
            name={itemName}
            tooltip={itemTooltip}
            rules={[...rules, { pattern: '^[+-]?[0-9]*[.]?[0-9]+$', message: 'Geçersiz ondalıklı sayı' }]}
          >
            <Input style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <UnitOfMeasureSelect />
        </Col>
      </Row>
    );
  }

  if (props.field.dataType === 'STRING' && !props.field.isRichTextEditor) {
    return (
      <Space direction="vertical" style={{ width: '100%', marginBottom: '1.5rem' }}>
        <Form.Item
          label={<ItemLabel />}
          name={itemName}
          tooltip={itemTooltip}
          rules={[...rules, { max: 4000, message: '4000 karakterden uzun' }]}
          style={{ marginBottom: 0 }}
        >
          <TextArea rows={1} onChange={onSearch} ref={stringInputRef} />
        </Form.Item>
        {(suggestions?.length || 0) > 0 && (
          <Form.Item label="Öneriler" style={{ marginBottom: 0, marginTop: '1rem' }}>
            <Space>
              {suggestions?.map(s => (
                <Button size="small" key={s} onClick={() => onSuggestionClick(s)}>
                  {s}
                </Button>
              ))}
            </Space>
          </Form.Item>
        )}
        {(predictions?.length || 0) > 0 && Array.isArray(predictions) && (
          <Form.Item label="Yapay zeka önerileri (Önerilen değer - Güven oranı):" style={{ marginBottom: 0, marginTop: '1rem' }}>
            <Space direction="vertical">
              {predictions?.map(p => (
                <Button size="small" key={p.value} onClick={() => onPredictionClick(p.value, p.value)}>
                  {`${p.value} (${p.confidence})`}
                </Button>
              ))}
            </Space>
          </Form.Item>
        )}
        {showPredictionError && <Text type="warning">Yapay zeka tarafından %5 güven eşiğini geçen bir tahmin oluşturulamadı.</Text>}
      </Space>
    );
  }

  if (props.field.dataType === 'URL') {
    return (
      <Form.Item label={<ItemLabel />} name={itemName} tooltip={itemTooltip} rules={[...rules, { type: 'url', message: 'Geçersiz URL' }]}>
        <Input />
      </Form.Item>
    );
  }

  if (props.field.dataType === 'TEXT' && !props.field.isRichTextEditor) {
    return (
      <Form.Item label={<ItemLabel />} name={itemName} tooltip={itemTooltip} rules={rules}>
        <TextArea rows={1} onChange={onTextAreaChange} />
      </Form.Item>
    );
  }

  if ((props.field.dataType === 'TEXT' || props.field.dataType === 'STRING') && props.field.isRichTextEditor) {
    return (
      <Form.Item label={<ItemLabel />} name={itemName} tooltip={itemTooltip} rules={rules}>
        {/* @ts-ignore */}
        <TextEditor />
      </Form.Item>
    );
  }

  if (props.field.dataType === 'FILE_UPLOAD') {
    // define with this maxFileCount, maxFileSize, fileExtensions multiple file upload item
    // limit file size and file count
    const uploadImage = async (options: any) => {
      const { onSuccess, onError, file, onProgress } = options;

      const fmData = new FormData();
      fmData.append('file', file);
      fmData.append('directory', props?.field?.id ?? '');
      try {
        await apiClient.post('files/upload', fmData, {
          onUploadProgress: ({ total, loaded }) => {
            onProgress({ percent: Math.round((loaded / total) * 100).toFixed(2) }, file);
          },
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        onSuccess('Ok');
      } catch (err) {
        console.log('Error: ', err);
        onError({ err });
      }
    };

    const uploadProps: UploadProps = {
      name: 'file',
      multiple: true,
      action: getApiBaseUrl() + '/api/files/upload',
      customRequest: uploadImage,
      accept: props.field?.allowedFileExtensions?.join(',') ?? '*',
      beforeUpload: file => {
        const isSizeValid = file.size / 1024 / 1024 < (props?.field?.maxFileSize ?? 10);
        if (!isSizeValid) {
          message.error(`Dosya boyutu ${props?.field?.maxFileSize ?? 10}MB'dan büyük olamaz`);
        }
        return isSizeValid;
      },
      onChange(info: any) {
        if (info.fileList.length > (props?.field?.maxFileCount ?? 1)) {
          message.error(`En fazla ${props?.field?.maxFileCount ?? 1} dosya yüklenebilir`);
          return;
        }
      },
    };

    return (
      <>
        <Form.Item name={itemName} label={<ItemLabel />} tooltip={itemTooltip} rules={rules}>
          <Upload.Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Buraya tıklayarak dosya yükleme işleminizi başlatabilirsiniz</p>
          </Upload.Dragger>
        </Form.Item>
      </>
    );
  }

  if (props.field.dataType === 'PICKLIST' && !props.field.isWorkFlow) {
    return (
      <>
        <Form.Item label={<ItemLabel />} name={itemName} tooltip={itemTooltip} rules={rules}>
          <Select
            className="picklist-select"
            showSearch
            filterOption={(input, option) => {
              const text = option?.children.props.children.find((c: any) => c?.props?.className === 'option-text')?.props.children;
              return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
            }}
            onChange={selectChange}
          >
            {(props.field.picklistValues?.length || 0) > 0 &&
              props.field.picklistValues?.map(pv => (
                <Select.Option key={pv.id} value={pv.id}>
                  <div>
                    {pv.image && (
                      <LocalImage
                        src={`${baseApiUrl}fields/picklist-image?name=${pv.image}`}
                        alt={pv.val}
                        style={{ width: props.field?.picklistImageWidth + 'px', marginRight: '0.75rem' }}
                      />
                    )}
                    {!pv.image && props.field?.picklistValues.some(v => !!v.image) && (props.field?.picklistImageWidth || 0) > 0 && (
                      <div style={{ display: 'inline-block', width: props.field?.picklistImageWidth + 'px', marginRight: '0.75rem' }}></div>
                    )}
                    <span className="option-text">{pv.val.replace(/\$\d+/g, '.....')}</span>
                  </div>
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
        {props.field?.isTemplate && (
          <Form.Item name={props.field?.id + '_templateValues'} tooltip="Şablon Değerler">
            <DynamicEditableText text={templateSelectedValues} onUpdate={handleTemplateUpdate} />
          </Form.Item>
        )}
      </>
    );
  }

  if (props.field.dataType === 'PICKLIST' && props.field.isWorkFlow) {
    const currentStep = props.field.picklistValues?.find(pv => pv.id === props?.value);
    return (
      <>
        <div style={{ marginTop: '2rem', marginBottom: '1.5rem' }}>
          <Steps size="small" current={currentStep ? props.field.picklistValues?.indexOf(currentStep) : 0}>
            {(props.field.picklistValues?.length || 0) > 0 &&
              props.field.picklistValues?.sort((a, b) => a.order - b.order)?.map(pv => <Steps.Step key={pv.id} title={pv.val} />)}
          </Steps>
        </div>
        <Form.Item label={<ItemLabel />} name={itemName} tooltip={itemTooltip} rules={rules}>
          {workflowPermission && (
            <Select
              className="picklist-select"
              showSearch
              filterOption={(input, option) => {
                const text = option?.children.props.children.find((c: any) => c?.props?.className === 'option-text')?.props.children;
                return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }}
            >
              {(props.field.picklistValues?.length || 0) > 0 &&
                props.field.picklistValues?.map(pv => (
                  <Select.Option key={pv.id} value={pv.id}>
                    <div>
                      {pv.image && (
                        <LocalImage
                          src={`${baseApiUrl}fields/picklist-image?name=${pv.image}`}
                          alt={pv.val}
                          style={{ width: props.field?.picklistImageWidth + 'px', marginRight: '0.75rem' }}
                        />
                      )}
                      {!pv.image && props.field?.picklistValues.some(v => !!v.image) && (props.field?.picklistImageWidth || 0) > 0 && (
                        <div
                          style={{ display: 'inline-block', width: props.field?.picklistImageWidth + 'px', marginRight: '0.75rem' }}
                        ></div>
                      )}
                      <span className="option-text">{pv.val}</span>
                    </div>
                  </Select.Option>
                ))}
            </Select>
          )}
        </Form.Item>
      </>
    );
  }

  if (props.field.dataType === 'MULTIPICKLIST') {
    return (
      <Space direction="vertical" style={{ width: '100%', marginBottom: '1.5rem' }}>
        <>
          <Form.Item label={<ItemLabel />} name={itemName} tooltip={itemTooltip} rules={rules}>
            <Select
              mode={'multiple'}
              className="multipicklist-select"
              filterOption={(input, option) => {
                const text = option?.children.props.children.find((c: any) => c?.props?.className === 'option-text')?.props.children;
                return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }}
              onChange={selectChange}
            >
              {(props.field.picklistValues?.length || 0) > 0 &&
                props.field.picklistValues?.map(pv => (
                  <Select.Option key={pv.id} value={pv.id}>
                    <div>
                      {pv.image && (
                        <LocalImage
                          src={`${baseApiUrl}fields/picklist-image?name=${pv.image}`}
                          alt={pv.val}
                          style={{ width: props.field?.picklistImageWidth + 'px', marginRight: '0.75rem' }}
                        />
                      )}
                      {!pv.image && props.field?.picklistValues.some(v => !!v.image) && (props.field?.picklistImageWidth || 0) > 0 && (
                        <div
                          className="option-placeholder"
                          style={{ display: 'inline-block', width: props.field?.picklistImageWidth + 'px', marginRight: '0.75rem' }}
                        ></div>
                      )}
                      <span className="option-text">{pv.val.replace(/\$\d+/g, '.....')}</span>
                    </div>
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          {props.field?.isTemplate && (
            <Form.Item name={props.field?.id + '_templateValues'} tooltip="Şablon Değerler">
              <DynamicEditableText text={templateSelectedValues} onUpdate={handleTemplateUpdate} />
            </Form.Item>
          )}
        </>
        {(predictions?.length || 0) > 0 && Array.isArray(predictions) && (
          <Form.Item label="Yapay zeka önerileri" style={{ marginBottom: 0, marginTop: '1rem' }}>
            <Space>
              {predictions?.map(p => (
                <Button size="small" key={p.value} onClick={() => onPredictionClick(p.value, p.value)}>
                  {`${p.value} (%${p.confidence.split('.')[0]})`}
                </Button>
              ))}
            </Space>
          </Form.Item>
        )}
        {showPredictionError && <Text type="warning">Yapay zeka tarafından %5 güven eşiğini geçen bir tahmin oluşturulamadı.</Text>}
      </Space>
    );
  }

  if (props.field.dataType === 'BOOLEAN') {
    return (
      <Form.Item label={<ItemLabel />} name={itemName} tooltip={itemTooltip}>
        <Select>
          <Select.Option value={'true'}>Evet</Select.Option>
          <Select.Option value={'false'}>Hayır</Select.Option>
        </Select>
      </Form.Item>
    );
  }

  if (props.field.dataType === 'DATE') {
    return (
      <Form.Item label={<ItemLabel />} name={itemName} tooltip={itemTooltip} rules={rules}>
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>
    );
  }

  if (props.field.dataType === 'DATETIME') {
    return (
      <Form.Item label={<ItemLabel />} name={itemName} tooltip={itemTooltip} rules={rules}>
        <DatePicker style={{ width: '100%' }} showTime showSecond={false} />
      </Form.Item>
    );
  }

  if (props.field.dataType === 'TIME') {
    return (
      <Form.Item label={<ItemLabel />} name={itemName} tooltip={itemTooltip} rules={rules}>
        <TimePicker style={{ width: '100%' }} showSecond={false} />
      </Form.Item>
    );
  }

  if (props.field.dataType === 'FOREIGNKEY' && !props.field.mainForeignKeyFieldId) {
    return (
      <Space direction="vertical" style={{ width: '100%', marginBottom: '1.5rem' }}>
        <>
          <Form.Item label={<ItemLabel />} name={itemName} tooltip={itemTooltip} rules={rules}>
            {!searchable ? (
              <Select
                loading={fkOptionsLoading}
                showSearch
                filterOption={(input, option) => {
                  if (!option?.label || !input) {
                    return false;
                  }
                  return option.label.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }}
                allowClear
                onChange={selectChange}
                options={fkOptions}
              />
            ) : (
              <DebounceSelect loading={fkOptionsLoading} allowClear fetchOptions={loadForeignKeyOptions} />
            )}
          </Form.Item>
        </>
        {props.field?.isTemplate && (
          <Form.Item name={props.field?.id + '_templateValues'} tooltip="Şablon Değerler">
            <DynamicEditableText text={templateSelectedValues} onUpdate={handleTemplateUpdate} />
          </Form.Item>
        )}
        {(predictions?.length || 0) > 0 && Array.isArray(predictions) && (
          <Form.Item label="Yapay zeka önerileri (Önerilen değer - Güven oranı):" style={{ marginBottom: 0 }}>
            <Space direction="vertical">
              {predictions?.map(p => (
                <Button size="small" key={p.value} onClick={() => onPredictionClick(p.value, p.value)}>
                  {`${p.value} (${p.confidence})`}
                </Button>
              ))}
            </Space>
          </Form.Item>
        )}
        {showPredictionError && <Text type="warning">Yapay zeka tarafından %5 güven eşiğini geçen bir tahmin oluşturulamadı.</Text>}
      </Space>
    );
  }

  if (props.field.dataType === 'MULTIFOREIGNKEY' && !props.field.mainForeignKeyFieldId) {
    return (
      <Space direction="vertical" style={{ width: '100%', marginBottom: '1.5rem' }}>
        <>
          <Form.Item label={<ItemLabel />} name={itemName} tooltip={itemTooltip} rules={rules}>
            {!searchable ? (
              <Select
                mode="multiple"
                loading={fkOptionsLoading}
                showSearch
                onChange={selectChange}
                filterOption={(input, option) => {
                  if (!option?.label || !input) {
                    return false;
                  }
                  return option.label.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }}
                allowClear
                options={fkOptions}
              />
            ) : (
              <DebounceSelect mode="multiple" loading={fkOptionsLoading} allowClear fetchOptions={loadForeignKeyOptions} />
            )}
          </Form.Item>
          {props.field?.isTemplate && (
            <Form.Item name={props.field?.id + '_templateValues'} tooltip="Şablon Değerler">
              <DynamicEditableText text={templateSelectedValues} onUpdate={handleTemplateUpdate} />
            </Form.Item>
          )}
        </>
        {(predictions?.length || 0) > 0 && Array.isArray(predictions) && (
          <Form.Item label="Yapay zeka önerileri" style={{ marginBottom: 0, marginTop: '1rem' }}>
            <Space>
              {predictions?.map(p => (
                <Button size="small" key={p.value} onClick={() => onPredictionClick(p.value, p.value)}>
                  {`${p.value} (%${p.confidence.split('.')[0]})`}
                </Button>
              ))}
            </Space>
          </Form.Item>
        )}
        {showPredictionError && <Text type="warning">Yapay zeka tarafından %5 güven eşiğini geçen bir tahmin oluşturulamadı.</Text>}
      </Space>
    );
  }

  if (props.field.dataType === 'PARENTROW') {
    return (
      <Form.Item label={<ItemLabel />} name={itemName} tooltip={itemTooltip} rules={rules}>
        <Select
          loading={fkOptionsLoading}
          showSearch
          filterOption={(input, option) => option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
        >
          {fkOptions?.map(o => (
            <Select.Option key={o.value} value={o.value}>
              {o.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    );
  }

  return null;
}

// @ts-ignore
const DynamicEditableText = ({ text, onUpdate }) => {
  const [placeholders, setPlaceholders] = useState<string[][]>([]);
  const [inputs, setInputs] = useState<{ [key: string]: string[] }>({});

  useEffect(() => {
    const placeholderLists: string[][] = [];
    const inputValues: { [key: string]: string[] } = {};

    text.forEach((txt: any, index: any) => {
      const regex = /\$\d+/g;
      let match;
      const placeholderList: string[] = [];
      while ((match = regex.exec(txt)) !== null) {
        const placeholder = match[0];
        placeholderList.push(placeholder);
        if (!inputValues[index]) {
          inputValues[index] = [];
        }
        if (!inputValues[index].includes('')) {
          inputValues[index].push('');
        }
      }
      placeholderLists.push(placeholderList);
    });

    setPlaceholders(placeholderLists);
    setInputs(inputValues);
  }, [text]);

  useEffect(() => {
    const output = text.map((txt: any, index: any) =>
      txt.replace(/\$\d+/g, (match: any) => inputs[index]?.[placeholders[index]?.indexOf(match)] || match),
    );
    onUpdate(output);
  }, [inputs, text, onUpdate, placeholders]);

  const handleChange = (textIndex: number, placeholderIndex: number, value: string) => {
    setInputs(prev => {
      const newInputs = { ...prev };
      newInputs[textIndex] = [...(newInputs[textIndex] || [])];
      newInputs[textIndex][placeholderIndex] = value;
      return newInputs;
    });
  };

  const renderText = () => {
    return text.map((txt: any, textIndex: any) => (
      <div key={textIndex}>
        {txt.split(/(\$\d+)/).map((part: any, i: any) => {
          const placeholderIndex = placeholders[textIndex]?.indexOf(part) ?? -1;
          if (placeholderIndex !== -1) {
            return (
              <Input
                key={i}
                value={inputs[textIndex]?.[placeholderIndex] || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(textIndex, placeholderIndex, e.target.value)}
                style={{ width: 100, margin: '0 5px' }}
              />
            );
          }
          return <span key={i}>{part}</span>;
        })}
      </div>
    ));
  };

  return (
    <Space direction="vertical" style={{ marginBottom: 25 }}>
      <div>{renderText()}</div>
    </Space>
  );
};
